import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';
import Toast from '../../components/common/Toast';
import { exportToCSV, parseCSV } from '../../utils/csv';

interface Company {
  id: string;
  code: string;
  name: string;
  nameKana: string;
  phone: string;
  fax: string;
  email: string;
  address: string;
  buildingName: string;
  postalCode: string;
  website: string;
  representativeName: string;
  representativeTitle: string;
  industry: string;
  capital: number;
  employeeCount: number;
  establishedDate: string;
  fiscalYearEnd: number;
  memo: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const initialFormData = {
  code: '',
  name: '',
  nameKana: '',
  postalCode: '',
  address: '',
  buildingName: '',
  phone: '',
  fax: '',
  email: '',
  website: '',
  representativeName: '',
  representativeTitle: '',
  industry: '',
  capital: 0,
  employeeCount: 0,
  establishedDate: '',
  fiscalYearEnd: 3,
  memo: '',
};

// CSVインポート用のカラムマッピング
const csvColumnMapping: Record<string, keyof typeof initialFormData | 'name' | 'code'> = {
  '企業ID': 'code',
  '企業名': 'name',
  '企業名（カナ）': 'nameKana',
  '郵便番号': 'postalCode',
  '住所': 'address',
  '建物名': 'buildingName',
  '電話番号': 'phone',
  'FAX': 'fax',
  'メール': 'email',
  'Webサイト': 'website',
  '代表者名': 'representativeName',
  '代表者役職': 'representativeTitle',
  '業種': 'industry',
  '資本金': 'capital',
  '従業員数': 'employeeCount',
  '設立日': 'establishedDate',
  '決算期': 'fiscalYearEnd',
  'メモ': 'memo',
};

interface ImportCompany {
  code?: string;
  name?: string;
  nameKana?: string;
  postalCode?: string;
  address?: string;
  buildingName?: string;
  phone?: string;
  fax?: string;
  email?: string;
  website?: string;
  representativeName?: string;
  representativeTitle?: string;
  industry?: string;
  capital?: string | number;
  employeeCount?: string | number;
  establishedDate?: string;
  fiscalYearEnd?: string | number;
  memo?: string;
}

export default function CompanyList() {
  const { canCreate, canUpdate, canDelete, isAdmin, isPromoter } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const quickSearch = searchParams.get('search') || '';
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  // Toast通知
  const [toast, setToast] = useState<{message: string; type: 'success' | 'error'} | null>(null);
  // 削除確認用
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);

  // CSVインポート用
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState<ImportCompany[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ページネーション・ソート・表示件数（URL同期）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    const p = typeof page === 'function' ? page(currentPage) : page;
    setSearchParams(prev => { prev.set('page', String(p)); return prev; }, { replace: true });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.set('page', '1'); return prev; }, { replace: true });
  };

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'code', label: '企業ID', type: 'text', placeholder: '企業IDで検索' },
    { key: 'name', label: '企業名', type: 'text', placeholder: '企業名で検索' },
    { key: 'nameKana', label: '企業名カナ', type: 'text', placeholder: 'カナで検索' },
    { key: 'phone', label: '電話番号', type: 'text', placeholder: '電話番号で検索' },
    { key: 'email', label: 'メールアドレス', type: 'text', placeholder: 'メールで検索' },
    { key: 'address', label: '住所', type: 'text', placeholder: '住所で検索' },
    { key: 'industry', label: '業種', type: 'text', placeholder: '業種で検索' },
    { key: 'capital', label: '資本金', type: 'number', placeholder: '金額' },
    { key: 'employeeCount', label: '従業員数', type: 'number', placeholder: '人数' },
    { key: 'establishedDate', label: '設立日', type: 'date' },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'active', label: '有効' },
      { value: 'inactive', label: '無効' },
    ]},
  ], []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (quickSearch) {
          params.append('search', quickSearch);
        }
        if (searchConditions.length > 0) {
          params.append('advancedSearch', JSON.stringify(searchConditions));
        }
        params.append('page', String(currentPage));
        params.append('pageSize', String(pageSize));
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
        const data = await api.get<{ items: Company[]; total: number; totalPages: number }>(
          `/api/companies?${params.toString()}`
        );
        if (cancelled) return;
        setCompanies(Array.isArray(data?.items) ? data.items : []);
        setTotalItems(data?.total ?? 0);
        setTotalPages(data?.totalPages ?? 1);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch companies:', error);
        setCompanies([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [searchConditions, currentPage, pageSize, sortConfig, quickSearch]);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      prev.set('page', '1');
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);

      const data = await api.get<{ items: Company[]; total: number; totalPages: number }>(
        `/api/companies?${params.toString()}`
      );
      setCompanies(Array.isArray(data?.items) ? data.items : []);
      setTotalItems(data?.total ?? 0);
      const newTotalPages = data?.totalPages ?? 1;
      setTotalPages(newTotalPages);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setCurrentPage(1);
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      let next: { key: string; direction: 'asc' | 'desc' };
      if (prev.key !== key) next = { key, direction: 'asc' };
      else if (prev.direction === 'asc') next = { key, direction: 'desc' };
      else next = defaultSort;
      setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.set('page', '1'); return p; }, { replace: true });
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      if (value === '') {
        setFormData(prev => ({ ...prev, [name]: 0 }));
      } else {
        const parsed = name === 'employeeCount' ? parseInt(value, 10) : parseFloat(value);
        setFormData(prev => ({ ...prev, [name]: isNaN(parsed) ? 0 : Math.max(0, parsed) }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.post('/api/companies', formData);
      setShowCreateModal(false);
      setFormData(initialFormData);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to create company:', error);
      setToast({message: '企業の登録に失敗しました', type: 'error'});
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (company: Company) => {
    let latest = company;
    try {
      const fetched = await api.get<Company>(`/api/companies/${company.id}`);
      if (fetched) latest = fetched;
    } catch {
      // API取得失敗時はキャッシュデータを使用
    }
    setSelectedCompany(latest);
    setFormData({
      code: latest.code || '',
      name: latest.name || '',
      nameKana: latest.nameKana || '',
      postalCode: latest.postalCode || '',
      address: latest.address || '',
      buildingName: latest.buildingName || '',
      phone: latest.phone || '',
      fax: latest.fax || '',
      email: latest.email || '',
      website: latest.website || '',
      representativeName: latest.representativeName || '',
      representativeTitle: latest.representativeTitle || '',
      industry: latest.industry || '',
      capital: latest.capital || 0,
      employeeCount: latest.employeeCount || 0,
      establishedDate: latest.establishedDate || '',
      fiscalYearEnd: latest.fiscalYearEnd || 3,
      memo: latest.memo || '',
    });
    setShowEditModal(true);
  };

  const handleViewDetail = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany || submitting) return;

    setSubmitting(true);
    try {
      await api.put(`/api/companies/${selectedCompany.id}`, formData);
      setShowEditModal(false);
      setSelectedCompany(null);
      setFormData(initialFormData);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to update company:', error);
      setToast({message: '企業の更新に失敗しました', type: 'error'});
    } finally {
      setSubmitting(false);
    }
  };

  const [deleteRelatedCounts, setDeleteRelatedCounts] = useState<{ constructions: number; requests: number } | null>(null);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const handleDelete = async (company: Company) => {
    setDeleteTarget(company);
    setDeleteRelatedCounts(null);
    setLoadingRelated(true);
    try {
      const [constructionsData, requestsData] = await Promise.all([
        api.get<{ total: number }>(`/api/constructions?companyId=${company.id}&pageSize=0`),
        api.get<{ total: number }>(`/api/requests?companyId=${company.id}&pageSize=0`),
      ]);
      setDeleteRelatedCounts({
        constructions: constructionsData?.total ?? 0,
        requests: requestsData?.total ?? 0,
      });
    } catch {
      setDeleteRelatedCounts(null);
    } finally {
      setLoadingRelated(false);
    }
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/companies/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to delete company:', error);
      setToast({message: '企業の削除に失敗しました', type: 'error'});
      setDeleteTarget(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://sekaie-work-app.valleyintest.workers.dev';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBaseUrl}/api/companies/export/csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `企業一覧_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setToast({ message: 'CSVエクスポートに失敗しました', type: 'error' });
    }
  };

  // CSVインポート処理
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ファイル入力をリセット
    e.target.value = '';

    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: 'ファイルサイズは5MB以下にしてください', type: 'error' });
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCSV<ImportCompany>(text, csvColumnMapping);
      
      // バリデーション
      const errors: string[] = [];
      const validData: ImportCompany[] = [];

      parsed.forEach((item, index) => {
        const rowNum = index + 2; // ヘッダー行を考慮
        if (!item.name || !item.name.trim()) {
          errors.push(`${rowNum}行目: 企業名が必須です`);
        } else {
          // 数値フィールドの変換
          validData.push({
            ...item,
            code: item.code?.trim() || '',
            capital: item.capital ? Number(String(item.capital).replace(/[^0-9]/g, '')) || 0 : 0,
            employeeCount: item.employeeCount ? Number(String(item.employeeCount).replace(/[^0-9]/g, '')) || 0 : 0,
            fiscalYearEnd: item.fiscalYearEnd ? Number(String(item.fiscalYearEnd).replace(/[^0-9]/g, '')) || 3 : 3,
          });
        }
      });

      setImportData(validData);
      setImportErrors(errors);
      setImportResult(null);
      setShowImportModal(true);
    } catch (err) {
      setToast({message: 'CSVファイルの読み込みに失敗しました: ' + (err instanceof Error ? err.message : '不明なエラー'), type: 'error'});
    }
  };

  const handleImportExecute = async () => {
    if (importData.length === 0 || importing) return;

    setImporting(true);
    
    try {
      // 一括インポートAPI（企業IDをキーとしてupsert）
      const items = importData.map(company => ({
        code: company.code || '',
        name: company.name,
        nameKana: company.nameKana || '',
        postalCode: company.postalCode || '',
        address: company.address || '',
        buildingName: company.buildingName || '',
        phone: company.phone || '',
        fax: company.fax || '',
        email: company.email || '',
        website: company.website || '',
        representativeName: company.representativeName || '',
        representativeTitle: company.representativeTitle || '',
        industry: company.industry || '',
        capital: Number(company.capital) || 0,
        employeeCount: Number(company.employeeCount) || 0,
        establishedDate: company.establishedDate || '',
        fiscalYearEnd: Number(company.fiscalYearEnd) || 3,
        memo: company.memo || '',
      }));
      
      const result = await api.post<{ created: number; updated: number; failed: number; errors: string[] }>(
        '/api/companies/import',
        { items }
      );
      
      setImportResult({ success: result.created + result.updated, failed: result.failed });
      
      if (result.created + result.updated > 0) {
        fetchCompanies();
      }
    } catch {
      setImportResult({ success: 0, failed: importData.length });
    }

    setImporting(false);
  };

  const handleImportModalClose = () => {
    setShowImportModal(false);
    setImportData([]);
    setImportErrors([]);
    setImportResult(null);
  };

  const downloadCSVTemplate = () => {
    const templateColumns = [
      { key: 'code' as const, header: '企業ID' },
      { key: 'name' as const, header: '企業名' },
      { key: 'nameKana' as const, header: '企業名（カナ）' },
      { key: 'postalCode' as const, header: '郵便番号' },
      { key: 'address' as const, header: '住所' },
      { key: 'buildingName' as const, header: '建物名' },
      { key: 'phone' as const, header: '電話番号' },
      { key: 'fax' as const, header: 'FAX' },
      { key: 'email' as const, header: 'メール' },
      { key: 'website' as const, header: 'Webサイト' },
      { key: 'representativeName' as const, header: '代表者名' },
      { key: 'representativeTitle' as const, header: '代表者役職' },
      { key: 'industry' as const, header: '業種' },
      { key: 'capital' as const, header: '資本金' },
      { key: 'employeeCount' as const, header: '従業員数' },
      { key: 'establishedDate' as const, header: '設立日' },
      { key: 'fiscalYearEnd' as const, header: '決算期' },
      { key: 'memo' as const, header: 'メモ' },
    ];
    
    // サンプルデータ1行付きのテンプレート
    const sampleData = [{
      code: 'C001',
      name: '株式会社サンプル',
      nameKana: 'カブシキガイシャサンプル',
      postalCode: '100-0001',
      address: '東京都千代田区千代田1-1',
      buildingName: 'サンプルビル3階',
      phone: '03-1234-5678',
      fax: '03-1234-5679',
      email: 'info@sample.co.jp',
      website: 'https://sample.co.jp',
      representativeName: '山田太郎',
      representativeTitle: '代表取締役',
      industry: 'IT',
      capital: '10000000',
      employeeCount: '50',
      establishedDate: '2020-01-01',
      fiscalYearEnd: '3',
      memo: '備考欄',
    }];
    
    exportToCSV(sampleData, templateColumns, '企業インポートテンプレート');
  };

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '-';
    return `${value.toLocaleString()}円`;
  };

  // デフォルト表示カラムキー（企業ID・企業名・住所）
  const defaultColumnKeys = ['code', 'name', 'address', 'actions'];

  const allColumns = [
    { 
      key: 'code', 
      header: '企業ID', 
      minWidth: '100px',
      sortable: true,
      render: (company: Company) => (
        <span className="text-sm font-mono text-gray-600 dark:text-gray-400">{company.code || '-'}</span>
      )
    },
    { 
      key: 'name', 
      header: '企業名', 
      minWidth: '180px',
      sortable: true,
      mobileTitle: true,
      render: (company: Company) => (
        <button
          onClick={() => handleViewDetail(company)}
          className="text-left hover:text-blue-600 dark:hover:text-blue-400"
        >
          <p className="font-medium">{company.name}</p>
          <p className="text-xs text-gray-500">{company.nameKana}</p>
        </button>
      )
    },
    { key: 'phone', header: '電話番号', minWidth: '120px', sortable: true },
    { key: 'email', header: 'メール', minWidth: '180px', sortable: true, hideOnMobile: true },
    { 
      key: 'address', 
      header: '住所', 
      minWidth: '200px', 
      hideOnMobile: true,
      render: (company: Company) => (
        <div className="truncate max-w-xs">
          <span className="block">{company.address}</span>
          {company.buildingName && (
            <span className="text-xs text-gray-500">{company.buildingName}</span>
          )}
        </div>
      )
    },
    { 
      key: 'employeeCount', 
      header: '従業員', 
      minWidth: '80px',
      sortable: true,
      hideOnMobile: true,
      render: (company: Company) => company.employeeCount ? `${company.employeeCount}名` : '-'
    },
    { 
      key: 'status', 
      header: 'ステータス', 
      minWidth: '100px',
      sortable: true,
      render: (company: Company) => (
        <Badge variant={company.status === 'active' ? 'success' : 'default'}>
          {company.status === 'active' ? '有効' : '無効'}
        </Badge>
      )
    },
    { 
      key: 'actions', 
      header: '', 
      minWidth: '120px', 
      render: (company: Company) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => handleViewDetail(company)} className="px-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="hidden lg:inline ml-1">詳細</span>
          </Button>
          {canUpdate('companies') && (
            <Button variant="ghost" size="sm" onClick={() => handleEdit(company)} className="px-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="hidden lg:inline ml-1">編集</span>
            </Button>
          )}
          {canDelete('companies') && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(company)} className="px-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden lg:inline ml-1">削除</span>
            </Button>
          )}
        </div>
      )
    },
  ];

  // showAllColumns が有効な場合は全カラム、それ以外はデフォルト3項目+アクション
  const columns = showAllColumns
    ? allColumns
    : allColumns.filter(col => defaultColumnKeys.includes(col.key));

  const renderForm = (onSubmit: (e: React.FormEvent) => void, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* 基本情報 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">基本情報</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="企業ID" name="code" value={formData.code} onChange={handleChange} placeholder="例: C001" />
          <Input label="企業名" name="name" value={formData.name} onChange={handleChange} required />
          <Input label="企業名（カナ）" name="nameKana" value={formData.nameKana} onChange={handleChange} />
        </div>
      </div>

      {/* 所在地 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">所在地</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="郵便番号" name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder="000-0000" />
          <div className="sm:col-span-2">
            <Input label="住所" name="address" value={formData.address} onChange={handleChange} />
          </div>
          <div className="sm:col-span-2">
            <Input label="建物名" name="buildingName" value={formData.buildingName} onChange={handleChange} placeholder="ビル名・階数など" />
          </div>
        </div>
      </div>

      {/* 連絡先 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">連絡先</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="電話番号" name="phone" value={formData.phone} onChange={handleChange} />
          <Input label="FAX" name="fax" value={formData.fax} onChange={handleChange} />
          <Input label="メールアドレス" name="email" type="email" value={formData.email} onChange={handleChange} />
          <Input label="Webサイト" name="website" type="url" value={formData.website} onChange={handleChange} />
        </div>
      </div>

      {/* 代表者 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">代表者</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="代表者名" name="representativeName" value={formData.representativeName} onChange={handleChange} />
          <Input label="代表者役職" name="representativeTitle" value={formData.representativeTitle} onChange={handleChange} placeholder="代表取締役社長など" />
        </div>
      </div>

      {/* 会社情報 */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">会社情報</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="業種" name="industry" value={formData.industry} onChange={handleChange} />
          <Input label="資本金（円）" name="capital" type="number" value={formData.capital || ''} onChange={handleChange} />
          <Input label="従業員数" name="employeeCount" type="number" value={formData.employeeCount || ''} onChange={handleChange} />
          <Input label="設立日" name="establishedDate" type="date" value={formData.establishedDate} onChange={handleChange} />
          <Select label="決算期" name="fiscalYearEnd" value={formData.fiscalYearEnd} onChange={handleChange}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <option key={month} value={month}>{month}月</option>
            ))}
          </Select>
        </div>
      </div>

      {/* メモ */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">メモ</h3>
        <textarea
          name="memo"
          value={formData.memo}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
          rows={3}
          placeholder="備考など"
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
        <Button 
          type="button" 
          variant="secondary" 
          onClick={() => { setShowCreateModal(false); setShowEditModal(false); setFormData(initialFormData); }} 
          className="w-full sm:w-auto"
        >
          キャンセル
        </Button>
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? '処理中...' : isEdit ? '更新' : '登録'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">企業管理</h1>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button variant="secondary" onClick={handleImportClick} className="flex-1 sm:flex-none">
            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <span className="hidden sm:inline">インポート</span>
          </Button>
          <Button variant="secondary" onClick={handleExportCSV} className="flex-1 sm:flex-none">
            <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">エクスポート</span>
          </Button>
          {canCreate('companies') && (
            <Button onClick={() => setShowCreateModal(true)} className="flex-1 sm:flex-none">
              <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="hidden sm:inline">企業を追加</span>
              <span className="sm:hidden">追加</span>
            </Button>
          )}
        </div>
      </div>

      <Card>
        {/* 詳細検索 */}
        <div className="mb-4">
          <AdvancedSearch
            fields={searchFields}
            onSearch={handleSearch}
            quickSearchPlaceholder="企業名で検索..."
            quickSearchField="name"
            initialConditions={searchConditions}
          />
        </div>
        {/* 管理者・プロモーター向け全項目表示切替 */}
        {(isAdmin || isPromoter) && (
          <div className="mb-3 flex items-center">
            <label className="inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showAllColumns}
                onChange={(e) => setShowAllColumns(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">全項目を表示</span>
            </label>
          </div>
        )}
        <Table 
          columns={columns} 
          data={companies} 
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="20">20件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
            <span className="text-gray-400">/ {totalItems}件</span>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </Card>

      {/* 作成モーダル */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); setFormData(initialFormData); }} title="企業を追加" size="lg">
        {renderForm(handleCreate)}
      </Modal>

      {/* 編集モーダル */}
      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setFormData(initialFormData); }} title="企業を編集" size="lg">
        {renderForm(handleUpdate, true)}
      </Modal>

      {/* 詳細モーダル */}
      <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedCompany(null); }} title="企業詳細" size="lg">
        {selectedCompany && (
          <div className="space-y-6">
            {/* 基本情報 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">基本情報</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500">企業ID</p>
                  <p className="font-medium font-mono text-gray-900 dark:text-white">{selectedCompany.code || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">企業名</p>
                  <p className="font-medium text-gray-900 dark:text-white break-all">{selectedCompany.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">企業名（カナ）</p>
                  <p className="font-medium text-gray-900 dark:text-white break-all">{selectedCompany.nameKana || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">ステータス</p>
                  <Badge variant={selectedCompany.status === 'active' ? 'success' : 'default'}>
                    {selectedCompany.status === 'active' ? '有効' : '無効'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* 所在地 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">所在地</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500">郵便番号</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.postalCode || '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-500">住所</p>
                  <p className="font-medium text-gray-900 dark:text-white break-all">{selectedCompany.address || '-'}</p>
                </div>
                {selectedCompany.buildingName && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-gray-500">建物名</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.buildingName}</p>
                  </div>
                )}
              </div>
            </div>

            {/* 連絡先 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">連絡先</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500">電話番号</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">FAX</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.fax || '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-500">メール</p>
                  <p className="font-medium text-gray-900 dark:text-white break-all">{selectedCompany.email || '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-500">Webサイト</p>
                  {selectedCompany.website ? (
                    <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline break-all">
                      {selectedCompany.website}
                    </a>
                  ) : (
                    <p className="font-medium text-gray-900 dark:text-white">-</p>
                  )}
                </div>
              </div>
            </div>

            {/* 代表者 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">代表者</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500">代表者名</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.representativeName || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">代表者役職</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.representativeTitle || '-'}</p>
                </div>
              </div>
            </div>

            {/* 会社情報 */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">会社情報</h3>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500">業種</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.industry || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">資本金</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">{formatCurrency(selectedCompany.capital)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">従業員数</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.employeeCount ? `${selectedCompany.employeeCount}名` : '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">設立日</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedCompany.establishedDate ? new Date(selectedCompany.establishedDate).toLocaleDateString('ja-JP') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">決算期</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedCompany.fiscalYearEnd ? `${selectedCompany.fiscalYearEnd}月` : '-'}</p>
                </div>
              </div>
            </div>

            {/* メモ */}
            {selectedCompany.memo && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">メモ</h3>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                  {selectedCompany.memo}
                </p>
              </div>
            )}

            {/* メタ情報 */}
            <div className="pt-4 border-t dark:border-gray-700 text-xs text-gray-500">
              <p>登録日: {new Date(selectedCompany.createdAt).toLocaleString('ja-JP')}</p>
              {selectedCompany.updatedAt && selectedCompany.updatedAt !== selectedCompany.createdAt && (
                <p>更新日: {new Date(selectedCompany.updatedAt).toLocaleString('ja-JP')}</p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                閉じる
              </Button>
              <Button onClick={() => { setShowDetailModal(false); handleEdit(selectedCompany); }}>
                編集
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* CSVインポートモーダル */}
      <Modal isOpen={showImportModal} onClose={handleImportModalClose} title="企業CSVインポート" size="lg">
        <div className="space-y-6">
          {/* 結果表示 */}
          {importResult && (
            <div className={`p-4 rounded-lg ${importResult.failed === 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'}`}>
              <div className="flex items-center gap-3">
                {importResult.failed === 0 ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    インポート完了
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    成功: {importResult.success}件 / 失敗: {importResult.failed}件
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* エラー表示 */}
          {importErrors.length > 0 && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="font-medium text-red-800 dark:text-red-200 mb-2">
                バリデーションエラー
              </p>
              <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside max-h-32 overflow-y-auto">
                {importErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* プレビューテーブル */}
          {!importResult && importData.length > 0 && (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-medium">{importData.length}件</span>の企業データが読み込まれました。
                  内容を確認して「インポート実行」をクリックしてください。
                </p>
              </div>
              
              <div className="max-h-80 overflow-auto border dark:border-gray-700 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">企業ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">企業名</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">電話番号</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">メール</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">住所</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {importData.slice(0, 50).map((company, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-500">{index + 1}</td>
                        <td className="px-4 py-2 text-sm font-mono text-gray-600 dark:text-gray-400">
                          {company.code || <span className="text-blue-500">新規</span>}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white font-medium">
                          {company.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-500">{company.phone || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{company.email || '-'}</td>
                        <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate">
                          {company.address || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {importData.length > 50 && (
                  <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50 dark:bg-gray-800 text-center">
                    他 {importData.length - 50} 件...
                  </div>
                )}
              </div>
            </>
          )}

          {/* 初期状態（データなし） */}
          {!importResult && importData.length === 0 && importErrors.length === 0 && (
            <div className="text-center py-8">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 mb-4">CSVファイルを選択してください</p>
              <div className="flex flex-col gap-2 items-center">
                <Button variant="secondary" onClick={handleImportClick}>
                  ファイルを選択
                </Button>
                <button onClick={downloadCSVTemplate} className="text-sm text-blue-600 hover:underline">
                  テンプレートをダウンロード
                </button>
              </div>
            </div>
          )}

          {/* フォーマット説明 */}
          <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
            <p className="font-medium mb-1">CSVフォーマット</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>1行目はヘッダー行（企業ID, 企業名, 企業名（カナ）, 郵便番号, 住所, ...）</li>
              <li>「企業名」は必須項目です</li>
              <li><strong>「企業ID」を指定すると、既存データを更新します</strong></li>
              <li>文字コードはUTF-8（BOM付き推奨）またはShift-JIS</li>
              <li>「テンプレートをダウンロード」でフォーマットを確認できます</li>
            </ul>
          </div>

          {/* アクションボタン */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button variant="secondary" onClick={handleImportModalClose}>
              {importResult ? '閉じる' : 'キャンセル'}
            </Button>
            {!importResult && importData.length > 0 && (
              <Button onClick={handleImportExecute} disabled={importing}>
                {importing ? (
                  <>
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    インポート中...
                  </>
                ) : (
                  <>インポート実行（{importData.length}件）</>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="削除確認" size="sm">
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            「{deleteTarget?.name}」を削除しますか？この操作は取り消せません。
          </p>
          {loadingRelated && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
              関連データを確認中...
            </div>
          )}
          {deleteRelatedCounts && (deleteRelatedCounts.constructions > 0 || deleteRelatedCounts.requests > 0) && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">関連データがあります</p>
              <ul className="text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                {deleteRelatedCounts.constructions > 0 && (
                  <li>工事: {deleteRelatedCounts.constructions}件</li>
                )}
                {deleteRelatedCounts.requests > 0 && (
                  <li>要請: {deleteRelatedCounts.requests}件</li>
                )}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>キャンセル</Button>
            <Button onClick={executeDelete} disabled={loadingRelated} className="bg-red-600 hover:bg-red-700">削除</Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}
